import ProjectTile from './ProjectTile'
import './Projects.css'

const featuredProject = {
  title: 'ApplyLens',
  description: `ApplyLens is a data-driven job application workspace built to streamline the job search lifecycle. Moving beyond rigid spreadsheets, the platform offers an intuitive command center where users can view and manage their interview pipelines and analyze common skills among the roles they are applying to. Designed with a high-fidelity user experience in mind, it transforms the stress of tracking applications into a structured, highly efficient workflow.`,
  imgSrc: 'applylens.png',
  href: 'https://applylens.vercel.app/',
  isExternal: true,
  tags: [
    'Next.js',
    'React',
    'TypeScript',
    'PostgreSQL',
    'Google OAuth',
    'Gemini API',
    'Claude Fable',
  ],
}

const sportsProjects = [
  {
    title: 'Blue Line Breakdown',
    description: `Blue Line Breakdown is a hockey scouting and roster-building toolkit centered on player identity. It pairs NHL performance and tracking data with archetype badges, cross-era comparables, leaderboards, team analysis, lineup simulations, and an expansion-draft experience, giving fans a clearer view of how individual styles shape a roster.`,
    imgSrc: 'blb.png',
    href: 'https://blue-line-breakdown.vercel.app/',
    isExternal: true,
    tags: [
      'React',
      'Python',
      'Flask',
      'Google Cloud',
      'Data Viz',
      'Hockey Analytics',
    ],
  },
  {
    title: 'Pitch Perspective',
    description: `Pitch Perspective maps the many ways footballers and teams influence a match. Tendency fingerprints, role fits, preferred-zone maps, league leaderboards, manager and team profiles, and cross-league comparisons reveal where players operate, which qualities define them, and how those traits translate between competitions.`,
    imgSrc: 'pitchperspective.png',
    href: 'https://pitch-perspective.vercel.app/',
    isExternal: true,
    tags: ['Vite', 'React', 'Data Viz', 'Soccer Analytics'],
  },

  {
    title: 'Full Court Focus',
    description: `Full Court Focus approaches basketball as a question of fit: what does a player do well, how does a team play, and which combinations work together? Player fingerprints, APFV impact ratings, season leaderboards, draft comparisons, and Lineup IQ surface the skills, identities, and synergies behind NBA performance.`,
    imgSrc: 'fcf.png',
    href: 'https://full-court-focus.vercel.app/',
    isExternal: true,
    tags: [
      'Vite',
      'Python',
      'FastAPI',
      'Google Cloud',
      'Docker',
      'Data Viz',
      'Basketball Analytics',
    ],
  },
  {
    title: 'RedZone Report',
    description: `Built for fantasy decisions, RedZone Report separates sustainable NFL production from box-score noise. Position-adjusted percentiles, career-based comparables, team styles, seasonal leaders, and forward-looking projections help managers evaluate role, upside, and risk before draft day.`,
    imgSrc: 'redzone.png',
    href: 'https://redzone-report.vercel.app/',
    isExternal: true,
    tags: ['Next.js', 'Python', 'GitHub Actions', 'PostgreSQL'],
  },
]

const generalProjects = [
  {
    title: 'Predicting Stock Performance with HMMs',
    description: `Predicting Stock Performance with HMMs explores how Hidden Markov Models can forecast next-day prices for FAANG companies. It pits traditional Gaussian-based HMMs against a sliding-window approach, surfacing the strengths and limitations of each method for modeling noisy financial time series.`,
    imgSrc: 'hmmstock.png',
    href: 'https://github.com/alanpham5/hmm-stock-report',
    tags: ['Python', 'HMM', 'Time Series', 'Finance'],
  },
  {
    title: '4RowRoyale',
    description: `4RowRoyale is an interactive competitive programming and simulation platform where users design, benchmark, and deploy intelligent bots to climb a ladder-style Connect Four tournament. The application features a browser-based Python code editor with real-time API contract validation, immediate test-matching against baseline algorithms, and an automated tournament scheduler that ranks scripts asynchronously.`,
    imgSrc: '4row.png',
    href: 'https://4rowroyale.vercel.app/',
    isExternal: true,
    tags: ['React', 'PostgreSQL', 'Google OAuth', 'Decision Tree Algorithms'],
  },
  {
    title: 'MLB Trade Generator (MLB Company Hackathon)',
    description: `Built during a company hackathon, the MLB Trade Generator visualizes the impact of hypothetical player trades using live data from the MLB Stats API. Spanning both frontend and backend, it delivers a fully interactive experience for analyzing player performance and projected trade outcomes.`,
    imgSrc: 'mlbproject.png',
    tags: ['React', 'Node.js', 'MLB API', 'Data Viz'],
  },
]

export default function Projects({ designLanguage }) {
  const isMaterial = designLanguage === 'material3'

  const sportsGroupJSX = (
    <div
      key="sports-group"
      className={`sports-group-container sports-group-container--${designLanguage}`}
    >
      <div className="sports-group-header">
        <h4 className="sports-group-title">Sports Analytics Suite</h4>
        <p className="sports-group-description">
          A collection of dedicated data platforms that transform raw box-score
          metrics into predictive, high-density blueprints and visual style
          profiles across professional sports.
        </p>
      </div>
      <div className="sports-group-grid">
        {sportsProjects.map((project) => (
          <ProjectTile
            key={project.title}
            designLanguage={designLanguage}
            isGrouped={true}
            {...project}
          />
        ))}
      </div>
    </div>
  )

  const featuredJSX = (
    <ProjectTile
      key={featuredProject.title}
      designLanguage={designLanguage}
      {...featuredProject}
    />
  )

  return (
    <section className="projects-page">
      <h3 className="projects-title">Recent Projects</h3>
      <div className="projects-grid">
        {isMaterial ? (
          <>
            {sportsGroupJSX}
            {featuredJSX}
          </>
        ) : (
          <>
            {featuredJSX}
            {sportsGroupJSX}
          </>
        )}

        {generalProjects.map((project) => (
          <ProjectTile
            key={project.title}
            designLanguage={designLanguage}
            {...project}
          />
        ))}
      </div>
    </section>
  )
}
